import { createContext, useContext } from "react"
import type { BoardColor, BoardConfig, StatusCategory } from "./api"

// The board's vocabulary, client side.
//
// Everything that used to be a hardcoded Record<Status, …> reads from the
// project's config now. The default below exists for the few surfaces that
// render task chips with no project in scope (the dashboard cards before their
// project loads); it mirrors DEFAULT_BOARD_CONFIG server-side.

export const DEFAULT_CONFIG: BoardConfig = {
  statuses: [
    { id: "todo", name: "To do", category: "todo", color: "gray" },
    { id: "in_progress", name: "In progress", category: "in_progress", color: "yellow" },
    { id: "in_review", name: "In review", category: "in_progress", color: "blue" },
    { id: "blocked", name: "Blocked", category: "in_progress", color: "red" },
    { id: "done", name: "Done", category: "done", color: "green" },
  ],
  types: [
    { id: "feature", name: "Feature", color: "blue" },
    { id: "bug", name: "Bug", color: "red" },
    { id: "improvement", name: "Improvement", color: "purple" },
  ],
  priorities: [
    { id: "urgent", name: "Urgent", color: "red" },
    { id: "high", name: "High", color: "orange" },
    { id: "medium", name: "Medium", color: "yellow" },
    { id: "low", name: "Low", color: "blue" },
    { id: "none", name: "No priority", color: "gray" },
  ],
  defaultPriority: "none",
  labels: [],
  fields: [],
}

const BoardConfigContext = createContext<BoardConfig>(DEFAULT_CONFIG)
export const BoardConfigProvider = BoardConfigContext.Provider

export function useBoardConfig(): BoardConfig {
  return useContext(BoardConfigContext)
}

// A status that is not in the config any more (deleted after tasks left it, or
// data older than a rename) still has to render as *something* legible.
const GHOST = { name: "", category: "todo" as StatusCategory, color: "gray" as BoardColor }

export function statusOf(config: BoardConfig, id: string) {
  return config.statuses.find((s) => s.id === id) ?? { ...GHOST, id, name: id }
}

export function typeOf(config: BoardConfig, id: string) {
  return config.types.find((t) => t.id === id) ?? { id, name: id, color: "gray" as BoardColor }
}

/** The project's priorities in rank order. A config cached from before
 *  priorities were configurable lists none, so the defaults stand in. */
export function prioritiesOf(config: BoardConfig) {
  return config.priorities?.length ? config.priorities : DEFAULT_CONFIG.priorities
}

export function priorityOf(config: BoardConfig, id: string) {
  return prioritiesOf(config).find((p) => p.id === id) ?? { id, name: id, color: "gray" as BoardColor }
}

// The palette, mapped to theme tokens once. Semantic classes rather than raw
// hues where they exist, so dark mode keeps working without this file knowing
// about themes.
export const COLOR_TEXT: Record<BoardColor, string> = {
  gray: "text-muted",
  blue: "text-interactive",
  cyan: "text-accent",
  green: "text-success",
  yellow: "text-warning",
  orange: "text-warning",
  red: "text-danger",
  purple: "text-accent",
  pink: "text-danger",
}

export const COLOR_PILL: Record<BoardColor, string> = {
  gray: "border-border bg-surface-muted text-muted",
  blue: "border-interactive/30 bg-interactive/10 text-interactive",
  cyan: "border-accent/30 bg-accent-soft text-accent",
  green: "border-success/30 bg-success-soft text-success",
  yellow: "border-warning/30 bg-warning-soft text-warning",
  orange: "border-warning/30 bg-warning-soft text-warning",
  red: "border-danger/30 bg-danger-soft text-danger",
  purple: "border-accent/30 bg-accent-soft text-accent",
  pink: "border-danger/30 bg-danger-soft text-danger",
}

export const COLOR_DOT: Record<BoardColor, string> = {
  gray: "bg-muted",
  blue: "bg-interactive",
  cyan: "bg-accent",
  green: "bg-success",
  yellow: "bg-warning",
  orange: "bg-warning",
  red: "bg-danger",
  purple: "bg-accent",
  pink: "bg-danger",
}

/** Overdue means "past due and not in a done-category status" — the category,
 *  not the status id, so custom boards keep the semantics. */
export function isOverdueIn(config: BoardConfig, dueDate: string | null, statusId: string) {
  if (!dueDate) return false
  if (statusOf(config, statusId).category === "done") return false
  return new Date(dueDate).getTime() < Date.now()
}
